import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useUserContext } from 'context';
import { useToggle } from 'hooks';
import { getCsrfToken, trackEvent } from 'utils';
import { urls } from 'app-constants';
import SearchForm from './SearchForm';
import RequestForm from './RequestForm';

const SUCCESS_TYPE_CLAIM = 'claim';
const SUCCESS_TYPE_SUBMIT_VENUE = 'submit';

const ClaimVenueForm = ({
  headerLabel = 'Claim Your Venue',
  regionChoices,
  timezoneChoices,
  mapboxToken,
  poiTypeChoices,
}) => {
  const { id: userId } = useUserContext();
  const [poiData, setPoiData] = useState(null);

  const [showRequestForm, toggleRequestForm] = useToggle(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [complete, setComplete] = useState(null);
  const [errorData, setErrorData] = useState(null);
  useEffect(() => {
    setErrorData(null)
  }, [poiData]);

  const doClaim = (id, onSuccess) => {
    setIsSubmitting(true);
    setErrorData(null);

    fetch(urls.claimVenue, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'X-CSRFToken': getCsrfToken(),
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id }),
    })
      .then(response => {
        setIsSubmitting(false);
        if (!response.ok) throw new Error(response.statusText);
        return response;
      })
      .then(response => response.json())
      .then(data => onSuccess(data))
      .catch(err => {
        console.error(err);
      });
  };

  const handleSubmitClaim = id => doClaim(id, data => {
    if (data.status === 'succeeded') {
      setComplete(SUCCESS_TYPE_CLAIM);
      trackEvent('prod_submit_venue_claim', {
        venueName: poiData.name,
        venueId: poiData.id,
      });
    } else {
      setComplete(null);
      setErrorData(data);
    }
  });

  const handleSubmitNew = data => {
    doClaim(data.id, () => {
      setComplete(SUCCESS_TYPE_SUBMIT_VENUE)
      setTimeout(() => window.scrollTo(0, 0), 200);
    });

    trackEvent('prod_submit_new_venue', {
      venueName: data.name,
      venueId: data.id,
    });
  };

  const handleCancelRequest = () => {
    toggleRequestForm(false)
    setTimeout(() => window.scrollTo(0, 0), 200);
  };

  // TODO update
  const msgText = {
    [SUCCESS_TYPE_CLAIM]: `We have received your request to claim <strong>${poiData?.name}</strong>. You will be notified by email when our team completes the verification process.`,
    [SUCCESS_TYPE_SUBMIT_VENUE]: `We have received your request. You will be notified by email when our team completes the verification process.`,
  }[complete];

  const successMsg = msgText && (
    <div>
      <h5 className="fw-bold">Thank You!</h5>
      <div dangerouslySetInnerHTML={{ __html: msgText }} />
    </div>
  );

  const form = showRequestForm ? (
    <RequestForm
      regionChoices={regionChoices}
      timezoneChoices={timezoneChoices}
      mapboxToken={mapboxToken}
      poiTypeChoices={poiTypeChoices}
      isAuthenticated={!!userId}
      onSubmit={handleSubmitNew}
      onCancel={handleCancelRequest}
    />
  ) : (
    <SearchForm
      poiData={poiData}
      isAuthenticated={!!userId}
      isSubmitting={isSubmitting}
      errorData={errorData}
      onSubmitClaim={handleSubmitClaim}
      onRequestNew={toggleRequestForm}
      onPoiChange={setPoiData}
    />
  );

  return (
    <div className="claim-venue-form-wrap">
      <header>
        <h5>{headerLabel}</h5>
      </header>
      {successMsg || form}
    </div>
  );
};

ClaimVenueForm.propTypes = {
  headerLabel: PropTypes.string,
  regionChoices: PropTypes.arrayOf(PropTypes.array),
  timezoneChoices: PropTypes.arrayOf(PropTypes.array),
  mapboxToken: PropTypes.string,
  poiTypeChoices: PropTypes.arrayOf(PropTypes.array),
};

export default ClaimVenueForm;
